import React from 'react';
import './LegalPage.css';

export default function PrivacyPolicy() {
  return (
    <div className="legal-page">
      <section className="page-hero">
        <div className="container">
          <span className="section-label">Legal</span>
          <h1>Privacy <span>Policy</span></h1>
          <p>Last updated: March 13, 2025</p>
        </div>
      </section>

      <section className="section">
        <div className="container legal-container">
          <div className="legal-sidebar">
            <h4>Contents</h4>
            <ul>
              <li><a href="#collection">Information We Collect</a></li>
              <li><a href="#use">How We Use Your Data</a></li>
              <li><a href="#sharing">Data Sharing</a></li>
              <li><a href="#security">Data Security</a></li>
              <li><a href="#cookies">Cookies Policy</a></li>
              <li><a href="#rights">Your Rights</a></li>
              <li><a href="#retention">Data Retention</a></li>
              <li><a href="#children">Children's Privacy</a></li>
              <li><a href="#changes">Policy Changes</a></li>
              <li><a href="#contact">Contact Us</a></li>
            </ul>
          </div>

          <div className="legal-content">
            <div className="legal-intro">
              <p>
                Beefortune ("Company", "we", "us", or "our") is committed to protecting the privacy and security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or engage our services.
              </p>
              <p>
                By accessing our website or using our services, you acknowledge that you have read and understood this Privacy Policy. If you do not agree with its terms, please discontinue use of our services.
              </p>
            </div>

            <div className="legal-section" id="collection">
              <h2>1. Information We Collect</h2>
              <h3>1.1 Information You Provide Directly</h3>
              <p>When you interact with Beefortune, you may provide us with:</p>
              <ul>
                <li><strong>Contact Information:</strong> Name, email address, phone number, company name, and job title.</li>
                <li><strong>Project Information:</strong> Details about your technology requirements, business needs, and project timelines.</li>
                <li><strong>Communication Data:</strong> Messages, emails, and other correspondence you send to us.</li>
                <li><strong>Account Credentials:</strong> Username and password for any client portals or dashboards we provide.</li>
              </ul>

              <h3>1.2 Information Collected Automatically</h3>
              <p>When you visit our website, we automatically collect:</p>
              <ul>
                <li><strong>Usage Data:</strong> Pages visited, time spent, links clicked, and navigation patterns.</li>
                <li><strong>Technical Data:</strong> IP address, browser type and version, operating system, device identifiers.</li>
                <li><strong>Cookies and Tracking:</strong> Session cookies, persistent cookies, and analytics tracking pixels.</li>
                <li><strong>Referral Data:</strong> The URL that referred you to our website.</li>
              </ul>
            </div>

            <div className="legal-section" id="use">
              <h2>2. How We Use Your Information</h2>
              <p>We use the information we collect for the following purposes:</p>
              <ul>
                <li>To provide, operate, and improve our technology services and website.</li>
                <li>To respond to inquiries, provide quotes, and manage client relationships.</li>
                <li>To communicate about project status, updates, and deliverables.</li>
                <li>To send relevant industry insights, newsletters, and marketing communications (with your consent).</li>
                <li>To conduct data analysis and improve our service offerings.</li>
                <li>To comply with legal obligations, prevent fraud, and protect our rights.</li>
                <li>To personalize your experience on our website.</li>
              </ul>
            </div>

            <div className="legal-section" id="sharing">
              <h2>3. How We Share Your Information</h2>
              <p>Beefortune does not sell, trade, or rent your personal information. We may share your data in limited circumstances:</p>
              <ul>
                <li><strong>Service Providers:</strong> Trusted third-party vendors (cloud hosting, CRM, analytics tools) bound by data processing agreements.</li>
                <li><strong>Business Partners:</strong> With your explicit consent, for collaborative project delivery.</li>
                <li><strong>Legal Requirements:</strong> When required by law, court order, or governmental authority.</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of business assets.</li>
                <li><strong>Protection of Rights:</strong> To enforce our agreements or protect the rights of Beefortune and our users.</li>
              </ul>
            </div>

            <div className="legal-section" id="security">
              <h2>4. Data Security</h2>
              <p>We implement industry-standard security measures to protect your personal information, including:</p>
              <ul>
                <li>TLS/SSL encryption for all data transmissions.</li>
                <li>AES-256 encryption for data at rest.</li>
                <li>Role-based access controls limiting data access to authorized personnel only.</li>
                <li>Regular security audits and penetration testing.</li>
                <li>ISO 27001-compliant information security management practices.</li>
              </ul>
              <p>However, no method of transmission over the internet is 100% secure. While we strive to use commercially acceptable means to protect your data, we cannot guarantee absolute security.</p>
            </div>

            <div className="legal-section" id="cookies">
              <h2>5. Cookies & Tracking Technologies</h2>
              <p>We use cookies and similar tracking technologies to enhance your browsing experience. Types of cookies we use:</p>
              <ul>
                <li><strong>Essential Cookies:</strong> Necessary for website functionality and cannot be disabled.</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how visitors interact with our website (Google Analytics).</li>
                <li><strong>Marketing Cookies:</strong> Used to deliver relevant advertisements (with your consent).</li>
                <li><strong>Preference Cookies:</strong> Remember your settings and preferences.</li>
              </ul>
              <p>You can manage cookie preferences through your browser settings or our cookie consent banner.</p>
            </div>

            <div className="legal-section" id="rights">
              <h2>6. Your Rights</h2>
              <p>Subject to applicable law, you have the following rights regarding your personal data:</p>
              <ul>
                <li><strong>Right to Access:</strong> Request a copy of the personal data we hold about you.</li>
                <li><strong>Right to Rectification:</strong> Request correction of inaccurate or incomplete data.</li>
                <li><strong>Right to Erasure:</strong> Request deletion of your personal data ("right to be forgotten").</li>
                <li><strong>Right to Portability:</strong> Request your data in a structured, machine-readable format.</li>
                <li><strong>Right to Object:</strong> Object to processing of your data for direct marketing purposes.</li>
                <li><strong>Right to Withdraw Consent:</strong> Withdraw consent for processing where consent is the legal basis.</li>
              </ul>
              <p>To exercise any of these rights, please contact our Data Protection Officer at: <a href="mailto:privacy@gotechnology.in">privacy@gotechnology.in</a></p>
            </div>

            <div className="legal-section" id="retention">
              <h2>7. Data Retention</h2>
              <p>We retain personal information for as long as necessary to fulfill the purposes outlined in this policy, unless a longer retention period is required by law. Specifically:</p>
              <ul>
                <li>Client project data: 7 years after project completion (for legal and tax purposes).</li>
                <li>Marketing preferences: Until withdrawal of consent.</li>
                <li>Website analytics: 26 months.</li>
                <li>Communication records: 3 years after last interaction.</li>
              </ul>
            </div>

            <div className="legal-section" id="children">
              <h2>8. Children's Privacy</h2>
              <p>Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from minors. If we become aware that we have collected data from a child under 18, we will take steps to delete that information promptly. Parents or guardians who believe their child has provided us with personal information should contact us immediately.</p>
            </div>

            <div className="legal-section" id="changes">
              <h2>9. Changes to This Policy</h2>
              <p>We may update this Privacy Policy periodically to reflect changes in our practices, technology, or legal requirements. When we make material changes, we will notify you by:</p>
              <ul>
                <li>Posting the updated policy on our website with a revised "Last Updated" date.</li>
                <li>Sending an email notification to registered users (for significant changes).</li>
              </ul>
              <p>Your continued use of our services after the effective date of any changes constitutes your acceptance of the updated policy.</p>
            </div>

            <div className="legal-section" id="contact">
              <h2>10. Contact Us</h2>
              <p>If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:</p>
              <div className="legal-contact">
                <p><strong>Beefortune</strong><br />
                4th Floor, TechPark One, Baner Road<br />
                Pune - 411045, Maharashtra, India</p>
                <p><strong>Data Protection Officer:</strong> privacy@gotechnology.in</p>
                <p><strong>General Inquiries:</strong> info@beefortune.in</p>
                <p><strong>Phone:</strong> +91 90999 09090</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
